import { useState, useEffect } from "react";
import { signal, useSignal } from "@preact/signals-react";
import { useIntl, FormattedMessage as FM } from "react-intl";

import proxy from "api/proxy";
import tools from "tools/common";
import funcs from "tools/functions";
import dates from "tools/dates";
import { getStorage } from "store/local";
import BasicModal from "components/Modals/BasicModal2";
import { DeliveryHeader } from "./components/DeliveryHeader";
import { DeliverySetup } from "./components/DeliverySetup";
import { DeliveryTable } from "./components/DeliveryTable";
import { DeliveryActionFooter } from "./components/DeliveryActionFooter";
import {
  useMessage,
  MessageContainer,
} from "componentsv2/experimental/Message";
import ModalPayment from "./components/ModalPayment";
// import ModalInfo from "./components/ModalInfo";
import { OrgangeCircularIcon } from "components/Icons";
import {
  generateDeliveryInvoiceHTML,
  getRenderDocument,
} from "./templates/invoiceDelivery";
import DetailedDeliveryManReport from "./components/DetailedDeliveryManReport";
import PritingAPI from "tools/printing";

const openModal = signal(false);

function DeliveryPanel() {
  const { messages, error: setError, hide, warning, success } = useMessage();
  const records = useSignal([]);
  const delivery = useSignal(null);
  const action = useSignal(null);
  const selectedRows = useSignal(new Map());
  const selectionVersion = useSignal(0);
  const currentEnv = useSignal(null);
  const localPrinter = useSignal(null);
  const [modalData, setModalData] = useState(null);
  const session = getStorage("ctxSession");
  const sinceDate = tools.dateAdd(new Date(), "days", -1);
  const intl = useIntl();

  async function getConfigShop() {
    const { data } = await proxy.search(
      "sale.device",
      [["shop", "=", session.shop]],
      ["environment", "shop.local_printer"],
    );

    if (data && data.length > 0) {
      return data[0];
    }
    return null;
  }

  async function switchTab(tabName, env) {
    const isRestaurant = env === "restaurant";
    const baseDomain = [
      ["kind", "=", "delivery"],
      [isRestaurant ? "sale_date" : "shipment_date", ">=", `${sinceDate}`],
      ["shop", "=", session.shop],
    ];

    let statusFilter = isRestaurant
      ? ["confirmed", "processing"]
      : ["confirmed", "processing", "done"];

    const filterDomain = [...baseDomain];

    if (tabName === "collection") {
      await setStatements();
      filterDomain.push(["state", "in", statusFilter]);

      if (delivery.value?.id) {
        filterDomain.push(["delivery_party", "=", delivery.value.id]);
      }

      if (isRestaurant)
        filterDomain.push([
          "OR",
          ["payment_method", "!=", "all_paid"],
          ["payment_method", "=", null],
        ]);
    } else {
      statusFilter = isRestaurant
        ? ["draft", "quotation", "confirmed", "processing"]
        : ["draft", "quotation", "confirmed", "processing", "done"];

      filterDomain.push(
        ["state", "in", statusFilter],
        [
          "OR",
          ["delivery_party", "=", null],
          ["delivery_party", "=", delivery.value?.id || null],
        ],
        ["order_status", "in", ["commanded"]],
        ["OR",
          ["delivery_state", "=", null],
          ["delivery_state", "=", "to_dispatch"],
        ],
      );
    }

    const { data, error } = await proxy.search("sale.sale", filterDomain, [
      "number",
      "party.rec_name",
      "sale_date",
      "shipment_date",
      "delivery_time",
      "shipment_address.rec_name",
      "consumer.rec_name",
      "source.rec_name",
      "total_amount",
      "delivery_amount",
      "tip_amount",
      "order_status",
      "waiting_time",
      "payment_method",
      "state",
      "residual_amount",
      "paid_amount",
      "invoice_method",
    ]);

    if (!error)
      records.value =
        tabName !== "collection"
          ? data
          : data.filter((item) => item.residual_amount > 0);

    action.value = tabName;
    selectedRows.value = new Map();
    selectionVersion.value += 1;
  }

  const setStatements = async () => {
    let statements = [];
    const session = getStorage("ctxSession");
    const { data: userRecords } = await proxy.search(
      "res.user",
      [["id", "=", session.user]],
      ["sale_device"],
    );

    if (userRecords && userRecords.length > 0) {
      const { data } = await proxy.search(
        "account.statement",
        [
          ["sale_device", "=", userRecords[0].sale_device],
          ["state", "=", "draft"],
        ],
        ["id", "rec_name", "journal.name", "journal.require_voucher", "name"],
      );
      statements = data.map((item) => ({
        ...item,
        rec_name: item["journal."]?.name || item.name,
        "journal.require_voucher": item["journal."]?.require_voucher ?? false,
        "journal.id": item["journal."]?.id,
      }));
    }
    setModalData({ statements: statements });
    return statements;
  };

  async function ordersByArrive() {
    if (selectedRows.value.size <= 0)
      return warning(intl.formatMessage({ id: "no_invoices_selected" }));
    await setStatements();
    openModal.value = true;
  }

  async function dispatchOrders() {
    const invoicesDraft = Array.from(selectedRows.value.values()).filter(
      (inv) => inv.state === "draft",
    );
    if (invoicesDraft.length > 0)
      return warning(intl.formatMessage({ id: "must_bill" }));

    const args = {
      sales: Array.from(selectedRows.value?.values()).map((inv) => inv.id),
      dispatched: dates.fmtDatetime2Tryton(dates.getNow()),
      delivery_party: delivery.value.id,
    };
    await proxy.methodCall({
      model: "sale.sale",
      method: "dispatch_delivery",
      args: [args],
    });
    await new Promise((resolve) => setTimeout(resolve, 3000));
    switchTab("to_dispatch", currentEnv.value);
    selectedRows.value = new Map();
    selectionVersion.value += 1;
  }

  async function onClose() {
    if (modalData?.statements) {
      selectedRows.value = new Map();
      selectionVersion.value += 1;
      await switchTab("collection", currentEnv.value);
    } else if (!modalData?.msg) {
      action.value = null;
      delivery.value = null;
      selectedRows.value = new Map();
      selectionVersion.value += 1;
    }
    setModalData(null);
    openModal.value = false;
  }

  function handleSelectionChange(newSelection) {
    const selectionMap = new Map();
    if (Array.isArray(newSelection)) {
      newSelection.forEach((row) => selectionMap.set(row.id, row));
    } else if (newSelection) {
      selectionMap.set(newSelection.id, newSelection);
    }
    selectedRows.value = selectionMap;
  }

  async function onBilling(row) {
    try {
      if (row.invoice_method === "manual" && row.state === "draft") {
        await proxy.methodCall({
          model: "sale.sale",
          method: "to_quote",
          args: [{ sale_id: row.id }],
        });
      }
      await proxy.methodCall({
        model: "sale.sale",
        method: "faster_process",
        args: [
          {
            sale_id: row.id,
            delivery_party: delivery.value.id,
            delivery_state: "to_dispatch",
          },
        ],
      });
      switchTab(action.value, currentEnv.value);
    } catch (e) {
      console.error(e);
    }
  }

  const MODEL = "sale.sale";
  // const DOC_TYPE = "invoice";
  const PRINT_DELAY = 600;

  async function onPrint(record) {
    try {
      const session = getStorage("ctxSession");
      const type_doc = record.invoice_method !== "manual" && ["processing", "done"].includes(record.state)
          ? "invoice"
          : "order";
      const imageLogo = session.company_logo
        ? funcs.base64ToBlobUrl(session.company_logo, "png")
        : null;

      const [{ data: ctxPrinting }, { data: sale }] = await Promise.all([
        proxy.methodCall({
          model: MODEL,
          method: "get_printing_context",
          args: [{ user_id: session.user }],
        }),
        proxy.methodCall({
          model: MODEL,
          method: "get_data",
          args: [{ type_doc, sale_id: record.id }],
        }),
      ]);

      if (!sale) {
        setError("No se pudo obtener la información de la factura.");
        return;
      }

      if (localPrinter.value) {
        await printToLocalPrinter(localPrinter.value, ctxPrinting, sale, type_doc);
        return;
      }

      await printInBrowser({
        record,
        sale,
        ctxPrinting,
        imageLogo,
        type_doc
      });
    } catch (error) {
      console.error("Error al imprimir:", error);
      setError("Error al generar la impresión.");
    }
  }

  async function printToLocalPrinter(printerName, ctxPrinting, sale, type_doc) {
    const printer = new PritingAPI(printerName);

    const payload = {
      context: {
        ...ctxPrinting,
        local_printer: printerName,
      },
      record: sale,
      type_doc,
    };

    const { data, error } = await printer.post("/print_invoice", payload);

    if (error || !data) {
      throw new Error(error || "LA IMPRESION DE FACTURA FALLÓ!");
    }

    success("Factura enviada a la impresora correctamente.");
  }

  async function printInBrowser({ sale, ctxPrinting, imageLogo }) {
    const htmlContent = generateDeliveryInvoiceHTML(
      sale,
      ctxPrinting,
      imageLogo,
    );

    const printWindow = await getRenderDocument(htmlContent);

    printWindow.onload = () => {
      setTimeout(() => {
        printWindow.focus();
        printWindow.print();
      }, PRINT_DELAY);
    };
  }

  useEffect(() => {
    async function init() {
      const device = await getConfigShop();
      console.log(device, "validate device");

      currentEnv.value = device?.environment;
      localPrinter.value = device["shop."].local_printer;
      await switchTab("to_dispatch", currentEnv.value);
    }
    init();
  }, []);

  useEffect(() => {
    if (delivery.value) {
      switchTab(action.value, currentEnv.value);
    }
  }, [delivery.value]);

  return (
    <div className="px-2 md:p-x4 bg-zinc-50 flex flex-col gap-4 w-full">
      <MessageContainer messages={messages} onMessageClose={hide} />

      <DeliveryHeader
        delivery={delivery.value}
        onResetDelivery={() => (delivery.value = null)}
      />

      <div className="flex flex-col gap-0">
        <div className="flex items-center justify-between border-b border-zinc-200">
          <div className="flex">
            <button
              type="button"
              onClick={() => switchTab("to_dispatch", currentEnv.value)}
              className={`px-8 py-4 font-bold transition-all border-b-2 text-xs uppercase tracking-widest ${
                action.value === "to_dispatch"
                  ? "border-zinc-900 text-zinc-900"
                  : "border-transparent text-zinc-400 hover:text-zinc-600"
              }`}
            >
              <FM id="delivery.panel.by_dispached" />
            </button>
            <button
              type="button"
              onClick={() => switchTab("collection", currentEnv.value)}
              className={`px-8 py-4 font-bold transition-all border-b-2 text-xs uppercase tracking-widest ${
                action.value === "collection"
                  ? "border-zinc-900 text-zinc-900"
                  : "border-transparent text-zinc-400 hover:text-zinc-600"
              }`}
            >
              <FM id="delivery.panel.dispached_liquidate" />
            </button>
          </div>
          <DetailedDeliveryManReport />
        </div>
      </div>
      <div className="bg-white rounded-xl shadow-sm border border-zinc-200 grow flex flex-col">
        {!delivery.value ? (
          <DeliverySetup delivery={delivery} />
        ) : (
          <>
            <div className="p-4 border-b border-zinc-100 bg-zinc-50/50 flex items-center">
              <div className="flex items-center">
                <h2 className="font-bold text-zinc-500 text-base">
                  {action.value === "to_dispatch" ? (
                    <FM id="sale.sale.delivery.panel_orders_ready" />
                  ) : (
                    <FM id="sale.sale.delivery.panel_pending_documents" />
                  )}
                </h2>
                <span className="px-3 py-2 bg-zinc-100 rounded-xl text-zinc-900 text-xs font-extrabold uppercase ml-3">
                  {delivery.value.rec_name}
                </span>
              </div>

              <div className="ml-auto tablet:p-2 ">
                <div className="bg-blue-50 border border-blue-100 rounded-xl flex items-center gap-2 justify-start px-1 tablet:text-sm">
                  <div className=" tablet:hidden md:block">
                    <OrgangeCircularIcon />
                  </div>
                  {action.value === "to_dispatch" ? (
                    <FM id="sale.sale.delivery.panel_instructions" />
                  ) : (
                    <FM id="sale.sale.delivery.panel_liquidate_instructions" />
                  )}
                </div>
              </div>
            </div>

            <div className="p-2 grow overflow-auto min-h-[400px] mb-20">
              {action.value && (
                <DeliveryTable
                  key={`${action.value}-${selectionVersion.value}`}
                  records={records.value}
                  selection={Array.from(selectedRows.value.values())}
                  onSelectionChange={handleSelectionChange}
                  activeTab={action.value}
                  onBilling={onBilling}
                  onPrint={onPrint}
                />
              )}
            </div>

            <DeliveryActionFooter
              selectedRows={selectedRows.value}
              activeTab={action.value}
              onDispatch={dispatchOrders}
              onCollect={ordersByArrive}
            />
          </>
        )}
      </div>

      <BasicModal
        openModal={openModal}
        closeModal={onClose}
        size={"w-5/6 h-3/4"}
        backdrop="blur"
      >
        {modalData?.statements ? (
          <ModalPayment
            data={modalData}
            closeModal={onClose}
            selectedRows={selectedRows.value}
          />
        ) : null}
      </BasicModal>
    </div>
  );
}

export default DeliveryPanel;
